/**
 * 训练序列槽位模板（§7 / 序列自定义）。
 *
 * 每个「训练日」由一组**槽位**定义（描述 + 首选动作 + 回退条件），
 * 生成器（plan.ts）负责把槽位解析为具体动作：
 *   1. 首选动作依次取第一个「当前伤情可用且未在本日使用」的；
 *   2. 否则按 muscle/pattern/isolationOnly 在动作库中保守回退搜索；
 *   3. 仍无可用则删除该槽位（记录原因，不伪造动作）。
 *
 * 现有 A–E 模板作为 DEFAULT_TEMPLATES 保留，首次作为种子写入用户库。
 * 提供确定性纯函数：
 *   - nextTemplate: 按 position 取最近完成正式会话所用模板的下一个（E 日不参与轮转）；
 *   - validateTrainingTemplate: 校验模板合法性（至少 1 槽位、E 日仅孤立动作）；
 *   - generateSlotsFromTargetMuscles: 简单模式根据目标部位自动生成槽位（复合优先、孤立靠后）。
 */

import type { Exercise, MovementPattern, PlanSlot, TrainingTemplate, WorkoutSession } from "@jilian/shared";

export type SequenceCode = "A" | "B" | "C" | "D" | "E";
export type { PlanSlot };

export const DEFAULT_TEMPLATES: Record<SequenceCode, PlanSlot[]> = {
  // A：胸 · 三头 · 腿屈伸
  A: [
    { id: "A1", description: "稳定推胸复合动作", muscle: "胸大肌", pattern: "horizontal_push", preferredIds: ["seated_machine_press", "chest_supported_hammer_press"] },
    { id: "A2", description: "第二个推胸动作", muscle: "胸大肌", pattern: "horizontal_push", preferredIds: ["chest_supported_hammer_press", "seated_single_arm_machine_press"] },
    { id: "A3", description: "夹胸孤立动作", muscle: "胸大肌", pattern: "horizontal_push", isolationOnly: true, preferredIds: ["pec_deck"] },
    { id: "A4", description: "三头孤立动作", muscle: "肱三头肌", isolationOnly: true, preferredIds: ["high_stability_triceps_pushdown", "seated_machine_triceps_extension", "seated_dumbbell_overhead_extension"] },
    { id: "A5", description: "坐姿腿屈伸", muscle: "股四头肌", pattern: "knee_extension", preferredIds: ["seated_leg_extension"] },
  ],
  // B：背 · 二头 · 腿弯举
  B: [
    { id: "B1", description: "高位下拉", muscle: "背阔肌", pattern: "vertical_pull", preferredIds: ["wide_grip_lat_pulldown", "neutral_grip_lat_pulldown"] },
    { id: "B2", description: "胸托或稳定坐姿划船", muscle: "背阔肌", pattern: "horizontal_pull", preferredIds: ["chest_supported_machine_row", "chest_supported_dumbbell_row", "seated_machine_row"] },
    { id: "B3", description: "背部孤立动作（首选；无孤立可用时回退同肌群安全复合，如对握下拉）", muscle: "背阔肌", pattern: "vertical_pull", preferredIds: ["machine_straight_arm_pulldown"] },
    { id: "B4", description: "坐姿或支撑二头动作", muscle: "肱二头肌", isolationOnly: true, preferredIds: ["machine_preacher_curl", "preacher_curl", "seated_cable_biceps_curl"] },
    { id: "B5", description: "腿弯举", muscle: "腘绳肌", pattern: "knee_flexion", preferredIds: ["prone_leg_curl", "seated_leg_curl"] },
  ],
  // C：肩 · 核心 · 髋外展
  C: [
    { id: "C1", description: "坐姿推肩", muscle: "三角肌前束", pattern: "vertical_push", preferredIds: ["seated_machine_shoulder_press", "seated_dumbbell_shoulder_press"] },
    { id: "C2", description: "坐姿侧平举", muscle: "三角肌中束", isolationOnly: true, preferredIds: ["seated_dumbbell_lateral_raise", "machine_lateral_raise"] },
    { id: "C3", description: "反向飞鸟或安全后束替代", muscle: "三角肌后束", isolationOnly: true, preferredIds: ["reverse_pec_deck", "chest_supported_rear_delt_fly"] },
    { id: "C4", description: "核心器械动作", muscle: "腹直肌", isolationOnly: true, preferredIds: ["seated_machine_crunch", "lying_crunch_no_foot_fix"] },
    { id: "C5", description: "坐姿髋外展", muscle: "臀中肌", pattern: "hip_abduction", preferredIds: ["seated_hip_abduction", "seated_hip_adduction"] },
  ],
  // D：上肢综合 · 腿部孤立（腿屈伸/腿弯举轮换）
  D: [
    { id: "D1", description: "推类动作", muscle: "胸大肌", pattern: "horizontal_push", preferredIds: ["seated_machine_press", "flat_dumbbell_press"] },
    { id: "D2", description: "拉类动作", muscle: "背阔肌", pattern: "vertical_pull", preferredIds: ["wide_grip_lat_pulldown", "neutral_grip_lat_pulldown"] },
    { id: "D3", description: "肩部动作", muscle: "三角肌前束", pattern: "vertical_push", preferredIds: ["seated_dumbbell_shoulder_press", "seated_machine_shoulder_press"] },
    { id: "D4", description: "手臂动作", muscle: "肱二头肌", isolationOnly: true, preferredIds: ["seated_dumbbell_alt_curl", "preacher_curl"] },
    { id: "D5", description: "腿屈伸或腿弯举（轮换）", muscle: "股四头肌", pattern: "knee_extension", preferredIds: ["seated_leg_extension", "prone_leg_curl"], rotateRecent: ["seated_leg_extension", "prone_leg_curl"] },
  ],
  // E：可选轻训练日（低疲劳孤立，组数约为正式日的 60%–70%，禁止力竭）
  E: [
    { id: "E1", description: "手臂（二头）", muscle: "肱二头肌", isolationOnly: true, preferredIds: ["preacher_curl", "machine_preacher_curl"] },
    { id: "E2", description: "手臂（三头）", muscle: "肱三头肌", isolationOnly: true, preferredIds: ["high_stability_triceps_pushdown", "seated_machine_triceps_extension"] },
    { id: "E3", description: "后束", muscle: "三角肌后束", isolationOnly: true, preferredIds: ["reverse_pec_deck", "chest_supported_rear_delt_fly"] },
    { id: "E4", description: "侧平举", muscle: "三角肌中束", isolationOnly: true, preferredIds: ["machine_lateral_raise", "seated_dumbbell_lateral_raise"] },
    { id: "E5", description: "核心", muscle: "腹直肌", isolationOnly: true, preferredIds: ["seated_machine_crunch"] },
  ],
};

/** 别名保留以保证兼容 */
export const SEQUENCE_TEMPLATES = DEFAULT_TEMPLATES;

/** 默认推荐分化模板列表（首次种子写入用户库） */
export function createDefaultTemplates(userId: string): TrainingTemplate[] {
  const now = new Date().toISOString();
  return [
    {
      id: `${userId}-tpl-A`,
      userId,
      code: "A",
      name: "胸 · 三头 · 腿屈伸",
      slots: DEFAULT_TEMPLATES.A,
      position: 1,
      isOptionalLightDay: false,
      source: "default",
      version: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${userId}-tpl-B`,
      userId,
      code: "B",
      name: "背 · 二头 · 腿弯举",
      slots: DEFAULT_TEMPLATES.B,
      position: 2,
      isOptionalLightDay: false,
      source: "default",
      version: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${userId}-tpl-C`,
      userId,
      code: "C",
      name: "肩 · 核心 · 髋外展",
      slots: DEFAULT_TEMPLATES.C,
      position: 3,
      isOptionalLightDay: false,
      source: "default",
      version: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${userId}-tpl-D`,
      userId,
      code: "D",
      name: "上肢综合 · 腿部孤立",
      slots: DEFAULT_TEMPLATES.D,
      position: 4,
      isOptionalLightDay: false,
      source: "default",
      version: 1,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${userId}-tpl-E`,
      userId,
      code: "E",
      name: "轻量低疲劳日（可选）",
      slots: DEFAULT_TEMPLATES.E,
      position: 5,
      isOptionalLightDay: true,
      source: "default",
      version: 1,
      createdAt: now,
      updatedAt: now,
    },
  ];
}

/** 正式日（A–D）模板的默认总组数参考（≈15–16 组），用于 E 日 60%–70% 上限计算。 */
export const FORMAL_DAY_REFERENCE_SETS = 15;

/** E 日总组数上限 = 正式日参考组数 × 70%（向下取整，保守）。 */
export const E_DAY_MAX_TOTAL_SETS = Math.floor(FORMAL_DAY_REFERENCE_SETS * 0.7);

/** E 日每个动作的目标组数倍率（60%–65%，保守侧）。 */
export const E_DAY_SETS_MULTIPLIER = 0.65;

/**
 * 确定性纯函数：计算下一个应练模板（§7 轮转算法）。
 *
 * 规则：
 *   1. 仅正式模板（isOptionalLightDay === false）参与轮转，按 position 升序排序；
 *   2. 从 recentSessions 中寻找最近完成（completed / ended_early）的正式会话；
 *   3. 会话所使用的模板 code 或 id 在正式列表中找到后，取其下一个（尾部循环回到第 1 个）；
 *   4. E 日不参与轮转计数（即若最近完成了 E 日，不影响正式轮转指针）；
 *   5. 若无任何完成历史，默认返回第 1 个正式模板；
 *   6. 若无任何正式模板，返回 templates[0]。
 */
export function nextTemplate(
  templates: ReadonlyArray<TrainingTemplate>,
  recentSessions: ReadonlyArray<WorkoutSession> = [],
): TrainingTemplate {
  if (templates.length === 0) {
    return createDefaultTemplates("default")[0]!;
  }

  const formalTemplates = templates
    .filter((t) => !t.isOptionalLightDay)
    .slice()
    .sort((a, b) => a.position - b.position);

  if (formalTemplates.length === 0) {
    return templates[0]!;
  }

  // 按完成时间/开始时间降序寻找最近一次正式训练
  const sortedSessions = recentSessions
    .filter((s) => s.status === "completed" || s.status === "ended_early")
    .slice()
    .sort((a, b) => {
      const timeA = a.finishedAt ?? a.startedAt ?? a.createdAt;
      const timeB = b.finishedAt ?? b.startedAt ?? b.createdAt;
      return timeB.localeCompare(timeA);
    });

  for (const session of sortedSessions) {
    // 从 session.notes 中提取 sequenceCode
    let code: string | null = null;
    if (session.notes) {
      const match = session.notes.match(/(?:^|;)sequence=([^;]+)/);
      if (match && match[1]) {
        code = match[1].trim();
      }
    }

    if (!code) continue;

    // 匹配正式模板（按 code 或 id）
    const matchedIdx = formalTemplates.findIndex(
      (t) => t.code === code || t.id === code,
    );

    if (matchedIdx !== -1) {
      const nextIdx = (matchedIdx + 1) % formalTemplates.length;
      return formalTemplates[nextIdx]!;
    }
  }

  // 默认返回第 1 个正式模板
  return formalTemplates[0]!;
}

/**
 * 模板合法性校验纯函数。
 * 校验规则：
 *   1. 每天至少 1 个动作槽位；
 *   2. isOptionalLightDay 的模板只允许孤立动作（首选动作全部不能为复合动作）。
 */
export function validateTrainingTemplate(
  template: TrainingTemplate,
  exerciseLibrary?: ReadonlyArray<Exercise>,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!template.slots || template.slots.length === 0) {
    errors.push("每天至少需要 1 个动作槽位");
  }

  if (template.isOptionalLightDay && exerciseLibrary && template.slots) {
    const byId = new Map(exerciseLibrary.map((e) => [e.id, e]));
    for (const slot of template.slots) {
      if (slot.isolationOnly === false) {
        errors.push(`可选轻训练日槽位「${slot.description}」不能指定非孤立动作`);
      }
      for (const prefId of slot.preferredIds) {
        const ex = byId.get(prefId);
        if (ex && ex.compoundType === "compound") {
          errors.push(`可选轻训练日只允许孤立动作（包含复合动作：${ex.nameZh}）`);
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/** 目标部位别名与匹配规则 */
interface MuscleGroupRule {
  key: string;
  nameZh: string;
  primaryMuscles: string[];
  patterns: MovementPattern[];
}

const MUSCLE_RULES: MuscleGroupRule[] = [
  { key: "胸", nameZh: "胸部", primaryMuscles: ["胸大肌"], patterns: ["horizontal_push"] },
  { key: "背", nameZh: "背部", primaryMuscles: ["背阔肌", "斜方肌"], patterns: ["vertical_pull", "horizontal_pull"] },
  { key: "肩", nameZh: "肩部", primaryMuscles: ["三角肌前束", "三角肌中束", "三角肌后束"], patterns: ["vertical_push"] },
  { key: "腿", nameZh: "腿部", primaryMuscles: ["股四头肌", "腘绳肌"], patterns: ["knee_extension", "knee_flexion"] },
  { key: "二头", nameZh: "手臂二头", primaryMuscles: ["肱二头肌"], patterns: [] },
  { key: "三头", nameZh: "手臂三头", primaryMuscles: ["肱三头肌"], patterns: [] },
  { key: "核心", nameZh: "核心", primaryMuscles: ["腹直肌"], patterns: [] },
  { key: "臀", nameZh: "臀部", primaryMuscles: ["臀中肌", "臀大肌", "髋内收肌"], patterns: ["hip_abduction", "hip_adduction"] },
];

/**
 * 简单模式：根据多选目标部位自动生成槽位（复合优先、孤立靠后）。
 */
export function generateSlotsFromTargetMuscles(
  targetMuscles: string[],
  exerciseLibrary: ReadonlyArray<Exercise>,
): PlanSlot[] {
  if (targetMuscles.length === 0) return [];

  const matchedRules = MUSCLE_RULES.filter((rule) =>
    targetMuscles.some((tm) => tm.includes(rule.key) || rule.key.includes(tm)),
  );

  const compoundSlots: PlanSlot[] = [];
  const isolationSlots: PlanSlot[] = [];
  let slotCounter = 1;

  for (const rule of matchedRules) {
    const candidates = exerciseLibrary.filter((e) =>
      !e.lockedUntilMedicalClearance &&
      e.primaryMuscles.some((m) => rule.primaryMuscles.includes(m)),
    );

    const compounds = candidates.filter((e) => e.compoundType === "compound");
    const isolations = candidates.filter((e) => e.compoundType === "isolation");

    // 复合动作槽位
    if (compounds.length > 0) {
      const topIds = compounds.slice(0, 2).map((e) => e.id);
      const slot: PlanSlot = {
        id: `slot_${slotCounter++}`,
        description: `${rule.nameZh}复合训练`,
        preferredIds: topIds,
        isolationOnly: false,
        dropWhenEmpty: true,
      };
      if (rule.primaryMuscles[0]) slot.muscle = rule.primaryMuscles[0];
      if (rule.patterns[0]) slot.pattern = rule.patterns[0];
      compoundSlots.push(slot);
    }

    // 孤立动作槽位
    if (isolations.length > 0) {
      const topIsoIds = isolations.slice(0, 2).map((e) => e.id);
      const slot: PlanSlot = {
        id: `slot_${slotCounter++}`,
        description: `${rule.nameZh}孤立训练`,
        preferredIds: topIsoIds,
        isolationOnly: true,
        dropWhenEmpty: true,
      };
      if (rule.primaryMuscles[0]) slot.muscle = rule.primaryMuscles[0];
      isolationSlots.push(slot);
    }
  }

  // 复合优先，孤立靠后
  return [...compoundSlots, ...isolationSlots];
}